import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Enhanced error display component for analytics features
 * Provides user-friendly error messages, retry mechanisms, and fallback options
 */
const AnalyticsErrorDisplay = ({
  error,
  onRetry,
  onClearError,
  canRetry = false,
  isRetrying = false,
  retryDelay = 0,
  showFallback = false,
  fallbackContent = null,
  className='',
  size = 'default' // 'compact', 'default', 'large'
}) => {
  const { theme } = useTheme();
  const [showDetails, setShowDetails] = useState(false);
  const [retryCountdown, setRetryCountdown] = useState(retryDelay);

  // Start countdown if retry delay is provided
  React.useEffect(() => {
    if (retryDelay > 0) {
      setRetryCountdown(retryDelay);
      const interval = setInterval(() => {
        setRetryCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [retryDelay]);

  if (!error) return null;

  const sizeClasses = {
    compact: 'p-3 text-sm',
    default: 'p-4',
    large: 'p-6 text-lg'
  };

  const iconSizes = {
    compact: 'w-4 h-4',
    default: 'w-5 h-5',
    large: 'w-6 h-6'
  };

  const getSeverityStyles = (severity) => {
    const baseStyles = 'border rounded-lg';
    
    switch (severity) {
      case 'critical':
        return `${baseStyles} bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200`;
      case 'error':
        return `${baseStyles} bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300`;
      case 'warning':
        return `${baseStyles} bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200`;
      case 'info':
        return `${baseStyles} bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200`;
      default:
        return `${baseStyles} bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200`;
    }
  };

  const getSeverityIcon = (severity) => {
    const iconClass = iconSizes[size];
    
    switch (severity) {
      case 'critical':
      case 'error':
        return (
          <svg className={`${iconClass} text-red-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'warning':
        return (
          <svg className={`${iconClass} text-yellow-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'info':
        return (
          <svg className={`${iconClass} text-blue-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className={`${iconClass} text-gray-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const handleRetryClick = () => {
    if (canRetry && !isRetrying && retryCountdown === 0) {
      onRetry?.();
    }
  };

  const handleActionClick = (actionType) => {
    switch (actionType) {
      case 'retry':
        handleRetryClick();
        break;
      case 'manual_prompt':
        // Handle manual prompt entry
        console.log('Manual prompt entry requested');
        break;
      case 'use_default':
        // Handle use default template
        console.log('Use default template requested');
        break;
      case 'manual_config':
        // Handle manual configuration
        console.log('Manual configuration requested');
        break;
      case 'contact_support':
        // Handle contact support
        window.open('mailto:support@parrotmate.com', '_blank');
        break;
      default:
        console.log('Unknown action:', actionType);
    }
  };

  return (
    <div className={`${getSeverityStyles(error.severity)} ${sizeClasses[size]} ${className}`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {getSeverityIcon(error.severity)}
        </div>
        
        <div className="ml-3 flex-1">
          {/* Error message */}
          <div className="font-medium mb-2">
            {error.displayMessage || error.message}
          </div>
          
          {/* Additional context */}
          {error.operation && size !== 'compact' && (
            <div className="text-sm opacity-75 mb-3">
              Operation: {error.operation.replace(/_/g, ' ')}
            </div>
          )}
          
          {/* Action buttons */}
          {error.actionButtons && error.actionButtons.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {error.actionButtons.map((button, index) => (
                <button
                  key={index}
                  onClick={() => handleActionClick(button.type)}
                  disabled={button.disabled || (button.type === 'retry' && (isRetrying || retryCountdown > 0))}
                  className={`px-3 py-1 text-sm rounded font-medium transition-colors ${
                    button.variant === 'primary'
                      ? 'bg-teal-600 hover:bg-teal-700 text-white disabled:bg-gray-400'
                      : button.variant === 'secondary'
                      ? 'bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-200 disabled:opacity-50'
                      : 'border border-current hover:bg-current hover:bg-opacity-10 disabled:opacity-50'
                  }`}
                >
                  {isRetrying && button.type === 'retry' ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-3 w-3" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Retrying...
                    </span>
                  ) : retryCountdown > 0 && button.type === 'retry' ? (
                    `Retry in ${retryCountdown}s`
                  ) : (
                    button.label
                  )}
                </button>
              ))}
            </div>
          )}
          
          {/* Fallback content toggle */}
          {showFallback && fallbackContent && (
            <div className="mb-3">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-sm underline hover:no-underline"
              >
                {showDetails ? 'Hide' : 'Show'} alternative content
              </button>
              
              {showDetails && (
                <div className="mt-2 p-3 bg-white dark:bg-gray-700 rounded border dark:bg-gray-800">
                  {fallbackContent}
                </div>
              )}
            </div>
          )}
          
          {/* Error details for development */}
          {process.env.NODE_ENV === 'development' && size !== 'compact' && (
            <details className="mt-3">
              <summary className="text-sm cursor-pointer hover:underline opacity-75">
                Error Details (Development)
              </summary>
              <div className="mt-2 p-3 bg-black bg-opacity-10 rounded text-xs font-mono overflow-auto max-h-32">
                <div><strong>Code:</strong> {error.code}</div>
                <div><strong>Category:</strong> {error.category}</div>
                <div><strong>Timestamp:</strong> {error.timestamp}</div>
                {error.context && (
                  <div><strong>Context:</strong> {JSON.stringify(error.context, null, 2)}</div>
                )}
              </div>
            </details>
          )}
        </div>
        
        {/* Close button */}
        {onClearError && (
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={onClearError}
              className="inline-flex text-current hover:opacity-75 focus:outline-none"
            >
              <svg className={iconSizes[size]} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Compact error display for inline use
 */
export const CompactAnalyticsError = (props) => (
  <AnalyticsErrorDisplay {...props} size="compact" />
);

/**
 * Large error display for full-page errors
 */
export const LargeAnalyticsError = (props) => (
  <AnalyticsErrorDisplay {...props} size="large" />
);

/**
 * Error display with fallback content
 */
export const AnalyticsErrorWithFallback = ({ fallbackContent, ...props }) => (
  <AnalyticsErrorDisplay 
    {...props} 
    showFallback={true} 
    fallbackContent={fallbackContent}
  />
);

export default AnalyticsErrorDisplay;
